import * as React from 'react'
import {cva} from 'class-variance-authority'
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome'
import {
  faExclamationTriangle,
  faExclamationCircle,
  faCheckCircle,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons'
import {exhaustive} from '@freckle/exhaustive'
import {cn} from '@frontrow/console/src/lib/utils'
import {Text} from '@frontrow/console/src/components/typography'

const alertVariants = cva('flex items-center p-3 bg-white border rounded', {
  variants: {
    variant: {
      warning: 'border-warning-brand',
      danger: 'border-destructive',
      success: 'border-success',
      info: 'border-info-brand'
    }
  },
  defaultVariants: {
    variant: 'info'
  }
})

type AlertTypeT = 'warning' | 'danger' | 'success' | 'info'

type Props = {
  type: AlertTypeT
  addClass?: string
  dataTest?: string | null
  children: React.ReactNode
}

export function Alert(props: Props): React.ReactElement {
  const {dataTest, children, type, addClass} = props

  const {icon, iconClass} = getIconAndStyles(type)

  const iconNode = (
    <div className="mr-2 self-start">
      <FontAwesomeIcon icon={icon} className={iconClass} style={{width: 16}} aria-hidden="true" />
    </div>
  )

  return (
    <div
      data-test={dataTest}
      role={type === 'danger' ? 'alert' : 'status'}
      aria-live={type === 'danger' ? 'assertive' : 'polite'}
      className={cn(alertVariants({variant: type}), addClass)}
    >
      {iconNode}

      <Text style="body-2" addClass="mt-0 mb-0 flex-1" htmlElement="div">
        {children}
      </Text>
    </div>
  )
}

type StylesT = {
  icon: React.ComponentProps<typeof FontAwesomeIcon>['icon']
  iconClass: string
}

function getIconAndStyles(type: AlertTypeT): StylesT {
  switch (type) {
    case 'warning':
      return {icon: faExclamationTriangle, iconClass: 'text-warning-brand'}
    case 'danger':
      return {icon: faExclamationCircle, iconClass: 'text-destructive'}
    case 'success':
      return {icon: faCheckCircle, iconClass: 'text-success'}
    case 'info':
      return {icon: faInfoCircle, iconClass: 'text-info-brand'}
    default:
      return exhaustive(type, 'AlertTypeT')
  }
}
